import { MotifButton, MotifToast } from "@ey-xd/motif-react";
import { useReadDistributionProcessingQuery } from "@/services/distribution/distributionReducers";
import { ClassFilter } from "./ClassFilter";
import { DataSource } from "./DataSource";
import { useCase } from "@/features/case/use-case";
import CalculationEditForm from "./Form/calculationEditForm";
import { RateTier } from "./RateTier";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";
import { CalculationSetting } from "./CalculationSetting";
import { CalculationStepLimitAmount } from "./CalculationStepLimitAmount";
import { ResultSetting } from "./ResultSetting";
import { RootState } from "@/app/store";
import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { commonFormateName, havingEditPermission } from "@/utility/common";
import { EDIT_BUTTON_VARIANT } from "@/components/Images/CommonImages";
import { DistributionStepContext } from "../summery/DistributionContext";

export const CalculationStepViewSummary = ({
  dataByStepId,
  summaryData,
  setEditForm,
  accountList,
  caseId,
  distributionId,
  stepId,
  calculationStepId,
  editForm,
  refetch,
}: any) => {
  const caseData = useCase();
  const { t } = useTranslation();
  const permissions = useSelector((state: any) => state.permissions);
  const { caseShortName, abbreviation } = useSelector(
    (state: any) => state.case
  );
  const { data: processingData, refetch: refetchDistributionProcessingData } =
    useReadDistributionProcessingQuery(distributionId, {
      skip: !distributionId,
    });
  const {
    CalculationStep,
    StepClassFilter,
    StepDataSource,
    StepLimitAmount,
    RateTier: RateTierPermissions,
    CalculationSettings,
    RateTierTemplate,
  } = permissions;
  const distributionStepContextData: any = useContext<any>(
    DistributionStepContext
  );

  const isDistributionStatusCommitted =
    useSelector(
      (state: RootState) => state.distributions.distributionStatus === 3
    ) ||
    distributionStepContextData?.distributionStatus === 3 ||
    [1, 2]?.includes(processingData?.processingStatus);

  const [statusMessage, setStatusMessage] = useState<any>({
    type: "error",
    message: "",
    show: false,
  });
  const [errorMsg, setErrorMsg] = useState("");

  const [exportDocument, { isFetching, originalArgs }] =
    useLazyExportDocumentQuery();
  const handleProgress = useCallback(
    (trigger: boolean) => {
      refetchDistributionProcessingData()
        .then((res: any) => {
          const processingStatus = res?.data?.processingStatus;
          if (processingStatus === 1 || processingStatus === 2) {
            setStatusMessage({
              show: true,
              message: t(
                "pages.distributions.distribution.caluclationPendingMessage"
              ),
              type: "info",
            });
          } else if (processingStatus === 3) {
            setStatusMessage({
              show: true,
              message: t("pages.distributions.distribution.caluclationError", {
                errorMessage: res?.data?.processingStatusMessage,
              }),
              type: "error",
            });
          } else {
            setStatusMessage({
              show: trigger,
              message: t(
                "pages.distributions.distribution.caluclationSuccessMessage",
                {
                  distributionName: summaryData.distributionName,
                }
              ),
              type: "success",
            });
          }
        })
        .catch((err: any) => {
          setStatusMessage({
            show: true,
            message: t("global.globalNotifications.wentWrong"),
            type: "error",
          });
        });
    },
    [refetchDistributionProcessingData, t, summaryData]
  );
  const interval = useRef<any>(null);
  useEffect(() => {
    if (
      processingData?.processingStatus === 1 ||
      processingData?.processingStatus === 2
    ) {
      interval.current = setInterval(() => {
        handleProgress(true);
      }, 10000);
    }
    return () => clearInterval(interval.current);
  }, [processingData, handleProgress]);
  useEffect(() => {
    handleProgress(false);
    //eslint-disable-next-line
  }, [handleProgress]);

  function stepPreview(url: string, entityType: string) {
    exportDocument({
      CalculationStepId: stepId,
      url: url,
      fileName:
        dataByStepId?.stepName && caseShortName
          ? commonFormateName(
              caseShortName,
              dataByStepId?.stepName,
              abbreviation,
              entityType
            )
          : "",
    })
      .then((response) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response.error.data as string);
            setErrorMsg(errObj.message || errObj.title);
            console.error("API call error:", errObj);
          } catch (parseError) {
            setErrorMsg("Something went wrong!!");
            console.error("Parsing error:", parseError);
          }
        }
      })
      .catch((error: any) => {
        setErrorMsg("Something went wrong!!");
        console.error("API call error:", error);
      });
  }
  const payeeType =
    (dataByStepId?.calculationGroupBy === 0 ||
      dataByStepId?.calculationGroupBy === 2) &&
    dataByStepId?.payeeTypeId === null
      ? "Creditor"
      : dataByStepId?.payeeTypeName;

  return (
    <>
      {errorMsg && (
        <MotifToast onClose={() => setErrorMsg("")} variant="error">
          {errorMsg}
        </MotifToast>
      )}
      {statusMessage.show && (
        <MotifToast
          onClose={() => setStatusMessage({ ...statusMessage, show: false })}
          variant={statusMessage?.type || "error"}
          supportingText={
            [1, 2].includes(processingData?.processingStatus) &&
            processingData?.processingStatusMessage
              ? t("global.globalNotifications.progressingMessage", {
                  status: processingData?.processingStatusMessage,
                })
              : undefined
          }
        >
          {statusMessage.message}
        </MotifToast>
      )}
      <div className="motif-container case-summary-form pad-v-20 mar-t-20">
        <div className="motif-row justify-content-between align-items-center">
          <div className="motif-col-lg-10">
            <div className="motif-body1-default-regular page-title">
              {t("pages.distributions.distributionStepsTab.stepFormView.title")}
            </div>
            <div className="motif-body2-default-regular page-description">
              {t(
                "pages.distributions.distributionStepsTab.stepFormView.description"
              )}
            </div>
          </div>
          <div className="motif-col-lg-1">
            {havingEditPermission(CalculationStep) && (
              <MotifButton
                size="small"
                type="submit"
                variant={EDIT_BUTTON_VARIANT}
                onClick={() => setEditForm(true)}
                disabled={isDistributionStatusCommitted}
              >
                {t("global.globalActions.edit")}
              </MotifButton>
            )}
          </div>
        </div>
      </div>
      <div className="motif-container main-container pad-v-20">
        {editForm ? (
          <CalculationEditForm
            summaryData={summaryData}
            setIsEdit={setEditForm}
            accountList={accountList}
            caseId={caseId || ""}
            distributionId={distributionId || ""}
            stepId={stepId || ""}
            calculationStepId={calculationStepId}
            refetch={refetch}
          />
        ) : (
          <>
            <div className="motif-row ">
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.stepName"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.stepName}
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.account"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.accountName}
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      `pages.distributions.distributionStepsTab.stepFormLabels.subAccount`
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.subAccountName}
                  </div>
                </div>
              </div>
            </div>
            <div className="motif-row pad-t-30">
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.calculationGroupBy"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.calculationGroupBy === 0
                      ? "Creditors"
                      : dataByStepId?.calculationGroupBy === 1
                      ? "Claims"
                      : dataByStepId?.calculationGroupBy === 2
                      ? "Assignee/Creditor"
                      : "Case"}
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.payeeType"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">{payeeType}</div>
                </div>
              </div>
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.memo"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.isMemo ? "Memo" : "Payable"}
                  </div>
                </div>
              </div>
            </div>
            <div className="motif-row pad-t-30">
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.comment"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.comment}
                  </div>
                </div>
              </div>
              <div className="motif-col-lg-4">
                <div>
                  <div className="title">
                    {t(
                      "pages.distributions.distributionStepsTab.stepFormLabels.description"
                    )}
                  </div>
                  <div className="motif-body2-default-bold">
                    {dataByStepId?.description}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <DataSource
        StepDataSource={StepDataSource}
        caseId={caseData.caseId}
        resolvedStatus={isDistributionStatusCommitted}
        stepId={stepId}
        stepData={dataByStepId}
      />
      <ClassFilter
        StepClassFilter={StepClassFilter}
        caseId={caseData.caseId}
        resolvedStatus={isDistributionStatusCommitted}
        stepId={stepId}
      />
      <RateTier
        RateTierPermissions={RateTierPermissions}
        RateTierTemplate={RateTierTemplate}
        distributionStatus={isDistributionStatusCommitted}
        caseId={caseId}
        resolvedStatus={isDistributionStatusCommitted}
        stepId={stepId}
      />
      <CalculationStepLimitAmount
        StepLimitAmount={StepLimitAmount}
        distributionStatus={isDistributionStatusCommitted}
        caseId={caseData.caseId}
        stepId={stepId}
        dataByStepId={dataByStepId}
      />
      <CalculationSetting
        CalculationSettings={CalculationSettings}
        distributionStatus={isDistributionStatusCommitted}
        caseId={caseId}
        stepId={stepId}
      />
      <ResultSetting
        CalculationSettings={CalculationSettings}
        distributionStatus={isDistributionStatusCommitted}
        caseId={caseData.caseId}
        stepId={stepId}
      />
      {havingEditPermission(CalculationStep) && (
        <div className="pad-t-20 sticky-footer button-container display-flex justify-content-end">
          <div className="motif-row">
            <MotifButton
              disabled={[1, 2]?.includes(processingData?.processingStatus)}
              onClick={() =>
                stepPreview("Distribution/PreviewStep", "Entitlement_Amounts")
              }
              loading={
                originalArgs?.url === "Distribution/PreviewStep" && isFetching
              }
            >
              {t(
                "pages.distributions.distributionStepsTab.previewEntitlementAmounts"
              )}{" "}
            </MotifButton>

            <MotifButton
              disabled={[1, 2]?.includes(processingData?.processingStatus)}
              onClick={() =>
                stepPreview(
                  "Distribution/ExportLastRanEntitlementAmount",
                  "Step"
                )
              }
              loading={
                originalArgs?.url ===
                  "Distribution/ExportLastRanEntitlementAmount" && isFetching
              }
              className="mar-l-10"
            >
              {t(
                "pages.distributions.distributionStepsTab.exportLastRanEntitlementAmounts"
              )}{" "}
            </MotifButton>
            {/* <MotifButton onClick={handleExport}>Export result</MotifButton> */}
          </div>
        </div>
      )}
      <br />
    </>
  );
};
